import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, KeyRound } from 'lucide-react';
import { FUNCTIONS_URL } from '../lib/supabase';

const BASE_URL = `${FUNCTIONS_URL}/api/v1`;

/* ---------------------------------- content ---------------------------------- */

const QUICKSTART_CURL = `curl "${BASE_URL}/lessons?limit=25" \\
  -H "Authorization: Bearer tt_live_YOUR_KEY_HERE"`;

const ERROR_ENVELOPE = `{
  "error": {
    "code": "not_found",
    "message": "No lesson with id 'b7f3…' exists in this organization."
  }
}`;

const NODE_VERIFY_SNIPPET = `const crypto = require('crypto');

function verifyWebhook(rawBody, signatureHeader, secret) {
  // signatureHeader e.g. "sha256=4fd0d7…"
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(rawBody, 'utf8')     // raw request body, before JSON.parse
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signatureHeader),
    Buffer.from(expected)
  );
}

// Express example — note express.raw, not express.json
app.post('/webhooks/tutortracer', express.raw({ type: 'application/json' }), (req, res) => {
  const ok = verifyWebhook(req.body, req.get('X-TutorTracer-Signature'), process.env.TT_WEBHOOK_SECRET);
  if (!ok) return res.status(401).end();
  const event = JSON.parse(req.body);
  // handle event.type …
  res.status(200).end();
});`;

const ISSUE_FLAGGED_PAYLOAD = `{
  "id": "evt_01j8kfz2m9",
  "type": "issue.flagged",
  "created_at": "2026-08-21T14:03:22Z",
  "data": {
    "issue": {
      "id": "9f2c1b4e-6a1d-4c0f-8f9a-2e7d5b3a1c88",
      "lesson_id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
      "category": "safeguarding",
      "severity": "high",
      "status": "open",
      "title": "Tutor arranged off-platform contact",
      "quote": "Just message me on my personal number instead.",
      "context": "Tutor suggested moving communication off the platform near the end of the lesson.",
      "timestamp": "51:20",
      "tutor_name": "James Whitfield",
      "student_name": "Amelia Ross",
      "flagged_at": "2026-08-21T14:03:20Z"
    }
  }
}`;

const ASSESSMENT_COMPLETED_PAYLOAD = `{
  "id": "evt_01j8kg31xq",
  "type": "assessment.completed",
  "created_at": "2026-08-21T14:05:10Z",
  "data": {
    "assessment": {
      "id": "3d1e8c2a-9b4f-4e6d-a7c8-5f0e1d2c3b4a",
      "lesson_id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
      "tutor_name": "James Whitfield",
      "student_name": "Amelia Ross",
      "overall_score": 2.4,
      "insufficient_evidence": false,
      "dimensions": [
        { "dimension": "subject_knowledge", "rating": "strong", "score": 3 },
        { "dimension": "explanation_quality", "rating": "adequate", "score": 2 },
        { "dimension": "student_engagement", "rating": "adequate", "score": 2 },
        { "dimension": "lesson_structure", "rating": "needs_development", "score": 1 },
        { "dimension": "professionalism", "rating": "strong", "score": 3 }
      ],
      "assessed_at": "2026-08-21T14:05:08Z"
    }
  }
}`;

const ENDPOINTS = [
  {
    method: 'GET',
    path: '/v1/organization',
    summary: 'The authenticated organization',
    desc: 'Returns the organization the API key belongs to, with configured thresholds and plan info.',
    params: [],
    example: `{
  "id": "a1b2c3d4-0000-4000-8000-000000000001",
  "name": "Bright Minds Tutoring",
  "timezone": "Europe/London",
  "lateness_threshold_minutes": 5,
  "created_at": "2025-11-02T09:14:00Z"
}`,
  },
  {
    method: 'GET',
    path: '/v1/rubric',
    summary: 'Quality rubric definition',
    desc: 'The five assessment dimensions, their descriptions, and the rating scale. Ratings map to scores: strong = 3, adequate = 2, needs_development = 1.',
    params: [],
    example: `{
  "scale": [
    { "rating": "strong", "score": 3 },
    { "rating": "adequate", "score": 2 },
    { "rating": "needs_development", "score": 1 }
  ],
  "dimensions": [
    { "key": "subject_knowledge", "label": "Subject Knowledge",
      "description": "Accuracy and depth of the tutor's subject understanding." },
    { "key": "explanation_quality", "label": "Explanation Quality",
      "description": "Clarity, scaffolding, and checking for understanding." },
    { "key": "student_engagement", "label": "Student Engagement",
      "description": "How actively the student participates and is drawn in." },
    { "key": "lesson_structure", "label": "Lesson Structure",
      "description": "Objectives, pacing, and use of the available time." },
    { "key": "professionalism", "label": "Professionalism",
      "description": "Punctuality, conduct, and appropriate communication." }
  ]
}`,
  },
  {
    method: 'GET',
    path: '/v1/lessons',
    summary: 'List lessons',
    desc: 'Lessons (sessions) newest first. Filterable by date range, tutor, and student.',
    params: [
      ['limit', 'integer', 'Page size, max 200. Default 50.'],
      ['cursor', 'string', 'Opaque cursor from a previous response’s next_cursor.'],
      ['expand', 'string', "Set to full to return complete lesson objects per row — full quality dimensions with evidence, all issues with lifecycle state, summaries (limit capped at 20)."],
      ['include_transcript', 'boolean', 'With expand=full, inline every timestamped transcript line per lesson (limit capped at 5). Default false.'],
      ['since', 'timestamp', 'Only lessons starting at or after this ISO-8601 time.'],
      ['until', 'timestamp', 'Only lessons starting before this ISO-8601 time.'],
      ['tutor', 'string', 'Exact tutor name.'],
      ['student', 'string', 'Exact student name.'],
    ],
    example: `{
  "data": [
    {
      "id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
      "external_id": "LSN-48213",
      "tutor_name": "James Whitfield",
      "student_name": "Amelia Ross",
      "subject": "GCSE Maths",
      "lesson_timestamp": "2026-08-21T13:00:04Z",
      "duration_minutes": 55,
      "punctuality": { "tutor_late_seconds": 0, "student_late_seconds": 95 },
      "quality": {
        "overall_pct": 80,
        "insufficient_evidence": false,
        "ratings": {
          "clarity": "strong",
          "engagement": "adequate",
          "adaptability": "adequate",
          "encouragement": "strong",
          "structure": "adequate"
        }
      },
      "issue_counts": { "open": 1, "total": 2 },
      "links": {
        "recording_url": "https://recordings.lessonspace.com/lsn-48213",
        "app_url": "https://app.tutortracer.com/lessons/b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d"
      }
    }
  ],
  "pagination": {
    "next_cursor": "eyJ2IjpbIjIwMjYtMDgtMjFUMTM6MDA6MDRaIl19",
    "has_more": true,
    "limit": 50
  }
}`,
  },
  {
    method: 'GET',
    path: '/v1/lessons/:id',
    summary: 'Retrieve a lesson',
    desc: 'Accepts either the internal UUID or the external id from your booking system (e.g. LSN-48213). Everything from the list row, plus the full quality assessment, complete issue objects, the AI lesson summary, and the complete timestamped transcript inline.',
    params: [
      ['include_transcript', 'boolean', 'The complete timestamped transcript is included by default; pass include_transcript=false to omit it.'],
    ],
    example: `{
  "id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
  "external_id": "LSN-48213",
  "tutor_name": "James Whitfield",
  "student_name": "Amelia Ross",
  "subject": "GCSE Maths",
  "lesson_timestamp": "2026-08-21T13:00:04Z",
  "duration_minutes": 55,
  "punctuality": { "tutor_late_seconds": 0, "student_late_seconds": 95 },
  "quality": {
    "overall_pct": 80,
    "insufficient_evidence": false,
    "ratings": {
      "clarity": "strong",
      "engagement": "adequate",
      "adaptability": "adequate",
      "encouragement": "strong",
      "structure": "adequate"
    },
    "dimensions": [
      {
        "key": "clarity",
        "label": "Clarity",
        "rating": "strong",
        "evidence": "The box-method walkthrough was clearly structured and the student reproduced it unaided on the next question.",
        "citations": [
          {
            "quote": "So each cell is one term multiplied out — try the next row yourself.",
            "moment": "Introducing the box method",
            "timestamp_display": "12:40",
            "timestamp_seconds": 760
          }
        ]
      }
    ],
    "overall_summary": "Clear, well-paced instruction with strong rapport; understanding checks were skipped in the middle third of the lesson.",
    "coaching_suggestion": "Pause after each worked example and ask the student to explain the step back before moving on.",
    "insufficient_reason": null
  },
  "issue_counts": { "open": 1, "total": 2 },
  "links": {
    "recording_url": "https://recordings.lessonspace.com/lsn-48213",
    "app_url": "https://app.tutortracer.com/lessons/b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d"
  },
  "issues": [
    {
      "id": "9f2c1b4e-6a1d-4c0f-8f9a-2e7d5b3a1c88",
      "session_id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
      "category": "safeguarding",
      "severity": 8,
      "status": "open",
      "state": "open",
      "care_level": 2,
      "why_flagged": "The tutor invited the student to move communication to a personal phone number, taking contact off the platform.",
      "tutor_quote": "Just message me on my personal number instead.",
      "context": "Discussing how to share extra practice papers near the end of the lesson.",
      "filter_status": "visible",
      "filter_decision_reason": null,
      "timestamp_display": "51:20",
      "timestamp_seconds": 3080,
      "playback_url": "https://recordings.lessonspace.com/lsn-48213?t=3080",
      "resolution_outcome": null,
      "resolution_notes": null,
      "resolved_at": null,
      "resolved_by": null,
      "actioned_at": null,
      "actioned_by": null,
      "breach_count": 1,
      "last_breach_check_at": "2026-08-21T14:05:02Z",
      "curriculum_check": {
        "verdict": "contradicts_curriculum",
        "confidence": 0.92,
        "summary": "The safeguarding policy expressly prohibits moving communication with a student off the platform.",
        "citations": [
          {
            "document_title": "Safeguarding Policy 2026",
            "excerpt": "Tutors must never move communication with a student to personal channels.",
            "reasoning": "Directly prohibits the behaviour in the flagged quote."
          }
        ]
      },
      "curriculum_checked_at": "2026-08-21T14:06:40Z",
      "created_at": "2026-08-21T14:03:20Z",
      "updated_at": "2026-08-21T14:06:40Z"
    }
  ],
  "flagged_for_review": false,
  "review_note": null,
  "lesson_summary": "Quadratic factorisation lesson. The tutor recapped last week's homework, introduced the box method, and set five practice questions for next session.",
  "transcript": {
    "line_count": 597,
    "lines": [
      { "timestamp": "00:12", "seconds": 12, "speaker": "James Whitfield",
        "role": "tutor",
        "text": "Right, last week we finished expanding brackets — how did the homework go?" },
      { "timestamp": "00:19", "seconds": 19, "speaker": "Amelia Ross",
        "role": "student",
        "text": "Mostly fine, I got stuck on question five." }
    ]
  }
}`,
  },
  {
    method: 'GET',
    path: '/v1/lessons/:id/transcript',
    summary: 'Lesson transcript',
    desc: 'The speaker-labelled transcript for a lesson. Accepts internal UUID or external id. Returns 404 if the lesson has no transcript yet.',
    params: [],
    example: `{
  "lesson_id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
  "segments": [
    { "timestamp": "00:12", "speaker": "tutor", "text": "Right, last week we finished expanding brackets — how did the homework go?" },
    { "timestamp": "00:19", "speaker": "student", "text": "Mostly fine, I got stuck on question five." }
  ]
}`,
  },
  {
    method: 'GET',
    path: '/v1/issues',
    summary: 'List issues',
    desc: 'AI-flagged issues, newest first. By default, tutor_lateness issues and issues suppressed by your guardrails are excluded — opt in with the flags below. Statuses are grouped: open, under_review, closed.',
    params: [
      ['limit', 'integer', 'Page size, max 200. Default 50.'],
      ['cursor', 'string', 'Opaque pagination cursor.'],
      ['since', 'timestamp', 'Only issues flagged at or after this time.'],
      ['until', 'timestamp', 'Only issues flagged before this time.'],
      ['status', 'string', 'One of open, under_review, closed (or a raw status value).'],
      ['category', 'string', 'Filter to a single issue category.'],
      ['min_severity', 'integer', 'Only issues at or above this severity (1-10).'],
      ['tutor', 'string', 'Exact tutor name.'],
      ['student', 'string', 'Exact student name.'],
      ['include_lateness', 'boolean', 'Include tutor_lateness issues. Default false.'],
      ['include_suppressed', 'boolean', 'Include guardrail-suppressed issues. Default false.'],
    ],
    example: `{
  "data": [
    {
      "id": "9f2c1b4e-6a1d-4c0f-8f9a-2e7d5b3a1c88",
      "session_id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
      "category": "safeguarding",
      "severity": 8,
      "status": "open",
      "state": "open",
      "care_level": 2,
      "why_flagged": "The tutor invited the student to move communication to a personal phone number, taking contact off the platform.",
      "tutor_quote": "Just message me on my personal number instead.",
      "context": "Discussing how to share extra practice papers near the end of the lesson.",
      "filter_status": "visible",
      "filter_decision_reason": null,
      "timestamp_display": "51:20",
      "timestamp_seconds": 3080,
      "playback_url": "https://recordings.lessonspace.com/lsn-48213?t=3080",
      "resolution_outcome": null,
      "resolution_notes": null,
      "resolved_at": null,
      "resolved_by": null,
      "actioned_at": null,
      "actioned_by": null,
      "breach_count": 1,
      "last_breach_check_at": "2026-08-21T14:05:02Z",
      "curriculum_check": {
        "verdict": "contradicts_curriculum",
        "confidence": 0.92,
        "summary": "The safeguarding policy expressly prohibits moving communication with a student off the platform.",
        "citations": [
          {
            "document_title": "Safeguarding Policy 2026",
            "excerpt": "Tutors must never move communication with a student to personal channels.",
            "reasoning": "Directly prohibits the behaviour in the flagged quote."
          }
        ]
      },
      "curriculum_checked_at": "2026-08-21T14:06:40Z",
      "created_at": "2026-08-21T14:03:20Z",
      "updated_at": "2026-08-21T14:06:40Z",
      "lesson": {
        "session_id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
        "external_id": "LSN-48213",
        "tutor_name": "James Whitfield",
        "student_name": "Amelia Ross",
        "lesson_timestamp": "2026-08-21T13:00:04Z"
      }
    }
  ],
  "pagination": {
    "next_cursor": null,
    "has_more": false,
    "limit": 50
  }
}`,
  },
  {
    method: 'GET',
    path: '/v1/issues/:id',
    summary: 'Retrieve an issue',
    desc: 'A single issue with its full quote and surrounding context.',
    params: [],
    example: `{
  "id": "9f2c1b4e-6a1d-4c0f-8f9a-2e7d5b3a1c88",
  "session_id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
  "category": "safeguarding",
  "severity": 8,
  "status": "open",
  "state": "open",
  "care_level": 2,
  "why_flagged": "The tutor invited the student to move communication to a personal phone number, taking contact off the platform.",
  "tutor_quote": "Just message me on my personal number instead.",
  "context": "Discussing how to share extra practice papers near the end of the lesson.",
  "filter_status": "visible",
  "filter_decision_reason": null,
  "timestamp_display": "51:20",
  "timestamp_seconds": 3080,
  "playback_url": "https://recordings.lessonspace.com/lsn-48213?t=3080",
  "resolution_outcome": null,
  "resolution_notes": null,
  "resolved_at": null,
  "resolved_by": null,
  "actioned_at": null,
  "actioned_by": null,
  "breach_count": 1,
  "last_breach_check_at": "2026-08-21T14:05:02Z",
  "curriculum_check": {
    "verdict": "contradicts_curriculum",
    "confidence": 0.92,
    "summary": "The safeguarding policy expressly prohibits moving communication with a student off the platform.",
    "citations": [
      {
        "document_title": "Safeguarding Policy 2026",
        "excerpt": "Tutors must never move communication with a student to personal channels.",
        "reasoning": "Directly prohibits the behaviour in the flagged quote."
      }
    ]
  },
  "curriculum_checked_at": "2026-08-21T14:06:40Z",
  "created_at": "2026-08-21T14:03:20Z",
  "updated_at": "2026-08-21T14:06:40Z"
}`,
  },
  {
    method: 'GET',
    path: '/v1/tutors',
    summary: 'List tutors',
    desc: 'Tutors with rolled-up lesson, issue, and quality stats. Assessments flagged insufficient_evidence are excluded from all averages.',
    params: [
      ['limit', 'integer', 'Page size, max 200. Default 50.'],
      ['cursor', 'string', 'Opaque pagination cursor.'],
    ],
    example: `{
  "data": [
    {
      "name": "James Whitfield",
      "lesson_count": 42,
      "open_issue_count": 1,
      "avg_quality_score": 2.6,
      "avg_lateness_minutes": 1.2,
      "last_lesson_at": "2026-08-21T13:00:04Z"
    }
  ],
  "next_cursor": null
}`,
  },
  {
    method: 'GET',
    path: '/v1/tutors/:name',
    summary: 'Retrieve a tutor',
    desc: 'Stats for one tutor, keyed by exact name (URL-encode it). Includes per-dimension quality averages.',
    params: [],
    example: `{
  "name": "James Whitfield",
  "lesson_count": 42,
  "open_issue_count": 1,
  "avg_quality_score": 2.6,
  "dimension_averages": {
    "subject_knowledge": 2.9,
    "explanation_quality": 2.5,
    "student_engagement": 2.4,
    "lesson_structure": 2.3,
    "professionalism": 2.8
  },
  "avg_lateness_minutes": 1.2,
  "last_lesson_at": "2026-08-21T13:00:04Z"
}`,
  },
  {
    method: 'GET',
    path: '/v1/students',
    summary: 'List students',
    desc: 'Students with lesson counts and most recent activity.',
    params: [
      ['limit', 'integer', 'Page size, max 200. Default 50.'],
      ['cursor', 'string', 'Opaque pagination cursor.'],
    ],
    example: `{
  "data": [
    {
      "name": "Amelia Ross",
      "lesson_count": 18,
      "tutors": ["James Whitfield"],
      "last_lesson_at": "2026-08-21T13:00:04Z"
    }
  ],
  "next_cursor": null
}`,
  },
  {
    method: 'GET',
    path: '/v1/students/:name',
    summary: 'Retrieve a student',
    desc: 'One student, keyed by exact name (URL-encode it), with their recent lessons.',
    params: [],
    example: `{
  "name": "Amelia Ross",
  "lesson_count": 18,
  "tutors": ["James Whitfield"],
  "recent_lessons": [
    { "id": "b7f3a2d1-4e5c-4a8b-9c0d-1e2f3a4b5c6d",
      "started_at": "2026-08-21T13:00:04Z", "subject": "GCSE Maths" }
  ],
  "last_lesson_at": "2026-08-21T13:00:04Z"
}`,
  },
  {
    method: 'GET',
    path: '/v1/quality/summary',
    summary: 'Quality summary',
    desc: 'Organization-wide quality aggregates over a date range, optionally grouped by tutor. Assessments flagged insufficient_evidence are excluded.',
    params: [],
    example: `{
  "from": "2026-07-22T00:00:00Z",
  "to": "2026-08-21T23:59:59Z",
  "assessed_lessons": 311,
  "excluded_insufficient_evidence": 9,
  "avg_overall_score": 2.5,
  "dimension_averages": {
    "subject_knowledge": 2.8,
    "explanation_quality": 2.5,
    "student_engagement": 2.4,
    "lesson_structure": 2.3,
    "professionalism": 2.7
  },
  "rating_distribution": {
    "strong": 0.46,
    "adequate": 0.41,
    "needs_development": 0.13
  }
}`,
  },
  {
    method: 'GET',
    path: '/v1/webhooks/events',
    summary: 'Webhook event catalog',
    desc: 'The event types your organization can subscribe to, discoverable programmatically.',
    params: [],
    example: `{
  "data": [
    { "type": "issue.flagged",
      "description": "A new issue was flagged on a lesson." },
    { "type": "assessment.completed",
      "description": "A quality assessment finished for a lesson." }
  ]
}`,
  },
];

/* ---------------------------------- components ---------------------------------- */

const CodeBlock = ({ code, isDark }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <div className="relative group">
      <pre className={`p-3 rounded-lg text-[11px] leading-relaxed font-mono overflow-x-auto border ${isDark ? 'bg-[#0a0a0a] text-[#d4d4d8] border-[#27272a]' : 'bg-[#f5f5f5] text-[#333] border-[#e5e5e5]'}`}>
        {code}
      </pre>
      <button
        onClick={copy}
        title="Copy"
        className={`absolute top-2 right-2 p-1.5 rounded-md border opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'bg-[#18181b] border-[#27272a] text-[#a1a1aa] hover:text-white' : 'bg-white border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a]'}`}
      >
        {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
};

const DeveloperApiDocs = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const mutedText = isDark ? 'text-[#a1a1aa]' : 'text-[#666]';
  const faintText = isDark ? 'text-[#52525b]' : 'text-[#999]';
  const strongText = isDark ? 'text-white' : 'text-[#1a1a1a]';
  const accent = isDark ? 'text-[#7662ac]' : 'text-[#586f8d]';
  const card = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inlineCode = `px-1 py-0.5 rounded text-[11px] font-mono ${isDark ? 'bg-[#18181b] text-[#d4d4d8]' : 'bg-[#f0f0f0] text-[#333]'}`;
  const h2 = `text-lg font-semibold font-[Manrope] tracking-tight ${strongText}`;
  const secondaryBtn = `text-xs px-3 py-1.5 rounded-md border flex items-center gap-1.5 ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#d0d0d0]'}`;

  const ParamsTable = ({ params }) => (
    <div className="overflow-x-auto mt-3">
      <table className="w-full text-left">
        <thead>
          <tr className={`text-[10px] uppercase tracking-wide ${faintText}`}>
            <th className="pb-1.5 pr-4 font-medium">Parameter</th>
            <th className="pb-1.5 pr-4 font-medium">Type</th>
            <th className="pb-1.5 font-medium">Description</th>
          </tr>
        </thead>
        <tbody>
          {params.map(([name, type, desc]) => (
            <tr key={name} className={`border-t text-xs align-top ${isDark ? 'border-[#27272a]' : 'border-[#f0f0f0]'}`}>
              <td className="py-1.5 pr-4"><code className={`font-mono ${accent}`}>{name}</code></td>
              <td className={`py-1.5 pr-4 ${faintText}`}>{type}</td>
              <td className={`py-1.5 ${mutedText}`}>{desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-8 pb-16">
      <div>
        <button onClick={() => navigate('/developer')} className={`flex items-center gap-2 text-sm mb-4 ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
          <ArrowLeft className="h-4 w-4" /> Back to Developer
        </button>
        <div className="flex items-center justify-between">
          <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${strongText}`}>API Documentation</h1>
          <button onClick={() => navigate('/developer/api')} className={secondaryBtn}>
            <KeyRound className="h-3.5 w-3.5" /> Manage keys &amp; webhooks
          </button>
        </div>
        <p className={`text-sm mt-2 ${mutedText}`}>
          A read-only REST API over your organization's lessons, issues, quality assessments, and people — plus signed webhooks for real-time events.
        </p>
      </div>

      {/* ---------- 1. Overview ---------- */}
      <section className={`${card} p-5 space-y-4`}>
        <h2 className={h2}>Overview</h2>

        <div>
          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${faintText}`}>Base URL</h3>
          <CodeBlock code={BASE_URL} isDark={isDark} />
        </div>

        <div>
          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${faintText}`}>Authentication</h3>
          <p className={`text-xs mb-2 ${mutedText}`}>
            Every request must carry an API key created on the <span className={accent}>API Access</span> page, sent as a bearer token. Keys are scoped to a single organization.
          </p>
          <CodeBlock code={'Authorization: Bearer tt_live_4fd0d7e2c9a1…'} isDark={isDark} />
        </div>

        <ul className={`text-xs space-y-1.5 ${mutedText} list-disc pl-4`}>
          <li>All requests and responses are <span className={strongText}>JSON</span> (<code className={inlineCode}>Content-Type: application/json</code>).</li>
          <li><span className={strongText}>Rate limit:</span> 120 requests per minute per key. Exceeding it returns <code className={inlineCode}>429</code> with a <code className={inlineCode}>Retry-After</code> header (seconds).</li>
          <li><span className={strongText}>Pagination</span> is cursor-based: pass <code className={inlineCode}>limit</code> (max 200) and read <code className={inlineCode}>next_cursor</code> from the response; pass it back as <code className={inlineCode}>cursor</code> to fetch the next page. A <code className={inlineCode}>null</code> cursor means no more pages.</li>
          <li>All timestamps are <span className={strongText}>ISO-8601 in UTC</span>, e.g. <code className={inlineCode}>2026-08-21T13:00:04Z</code>.</li>
        </ul>

        <div>
          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${faintText}`}>Errors</h3>
          <p className={`text-xs mb-2 ${mutedText}`}>
            Non-2xx responses use a consistent envelope. Codes include <code className={inlineCode}>unauthorized</code>, <code className={inlineCode}>forbidden</code>, <code className={inlineCode}>not_found</code>, <code className={inlineCode}>invalid_request</code>, and <code className={inlineCode}>rate_limited</code>.
          </p>
          <CodeBlock code={ERROR_ENVELOPE} isDark={isDark} />
        </div>
      </section>

      {/* ---------- 2. Quick start ---------- */}
      <section className={`${card} p-5 space-y-3`}>
        <h2 className={h2}>Quick start</h2>
        <p className={`text-xs ${mutedText}`}>
          Create a key on the API Access page, then list your most recent lessons:
        </p>
        <CodeBlock code={QUICKSTART_CURL} isDark={isDark} />
      </section>

      {/* ---------- 3. Endpoint reference ---------- */}
      <section className="space-y-4">
        <div className={`${card} p-5`}>
          <h2 className={h2}>Endpoint reference</h2>
          <p className={`text-xs mt-2 ${mutedText}`}>
            All endpoints are read-only <code className={inlineCode}>GET</code> requests scoped to your organization. Semantics worth knowing:
          </p>
          <ul className={`text-xs space-y-1.5 mt-2 ${mutedText} list-disc pl-4`}>
            <li>Lesson endpoints accept either the internal UUID <em>or</em> the external id from your booking system in the <code className={inlineCode}>:id</code> slot.</li>
            <li><code className={inlineCode}>/v1/issues</code> excludes <code className={inlineCode}>tutor_lateness</code> issues and guardrail-suppressed issues by default — pass <code className={inlineCode}>include_lateness=true</code> / <code className={inlineCode}>include_suppressed=true</code> to include them.</li>
            <li>Issue statuses are grouped into <code className={inlineCode}>open</code>, <code className={inlineCode}>under_review</code>, and <code className={inlineCode}>closed</code>.</li>
            <li>Quality ratings are <code className={inlineCode}>strong</code> / <code className={inlineCode}>adequate</code> / <code className={inlineCode}>needs_development</code>, scored 3 / 2 / 1. Assessments flagged <code className={inlineCode}>insufficient_evidence</code> carry null ratings and are excluded from every average.</li>
            <li>Assessment <code className={inlineCode}>dimensions</code> entries carry a narrative <code className={inlineCode}>evidence</code> field with inline <code className={inlineCode}>[MM:SS]</code> markers, plus a <code className={inlineCode}>citations</code> array whose timestamps have been verified against the transcript.</li>
          </ul>
        </div>

        {ENDPOINTS.map(ep => (
          <div key={ep.path} className={`${card} p-5`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isDark ? 'bg-[#7662ac]/15 text-[#a794d9]' : 'bg-[#586f8d]/10 text-[#586f8d]'}`}>{ep.method}</span>
              <code className={`text-sm font-mono font-medium ${strongText}`}>{ep.path}</code>
            </div>
            <h3 className={`text-xs font-medium mt-2 ${strongText}`}>{ep.summary}</h3>
            <p className={`text-xs mt-1 ${mutedText}`}>{ep.desc}</p>
            {ep.params.length > 0 && <ParamsTable params={ep.params} />}
            <div className="mt-3">
              <h4 className={`text-[10px] uppercase tracking-wide mb-1.5 ${faintText}`}>Example response</h4>
              <CodeBlock code={ep.example} isDark={isDark} />
            </div>
          </div>
        ))}
      </section>

      {/* ---------- 4. Webhooks ---------- */}
      <section className={`${card} p-5 space-y-5`}>
        <div>
          <h2 className={h2}>Webhooks</h2>
          <p className={`text-xs mt-2 ${mutedText}`}>
            Register HTTPS endpoints on the API Access page to receive events as they happen. Each delivery is a <code className={inlineCode}>POST</code> with a JSON body and a signature header.
          </p>
        </div>

        <div>
          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${faintText}`}>Event catalog</h3>

          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <code className={`text-xs font-mono font-medium ${strongText}`}>issue.flagged</code>
                <span className={`text-[11px] ${faintText}`}>a new issue was flagged on a lesson</span>
              </div>
              <CodeBlock code={ISSUE_FLAGGED_PAYLOAD} isDark={isDark} />
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <code className={`text-xs font-mono font-medium ${strongText}`}>assessment.completed</code>
                <span className={`text-[11px] ${faintText}`}>a quality assessment finished for a lesson</span>
              </div>
              <CodeBlock code={ASSESSMENT_COMPLETED_PAYLOAD} isDark={isDark} />
            </div>
          </div>
        </div>

        <div>
          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${faintText}`}>Delivery semantics</h3>
          <ul className={`text-xs space-y-1.5 ${mutedText} list-disc pl-4`}>
            <li>Delivery is <span className={strongText}>at-least-once</span> — make your handler idempotent, keyed on the event <code className={inlineCode}>id</code>.</li>
            <li>Any 2xx response marks the delivery <code className={inlineCode}>delivered</code>. Anything else (or a timeout) schedules retries at <span className={strongText}>1m, 5m, 30m, 2h, 12h</span>; after the final retry fails, the delivery is marked <code className={inlineCode}>dead</code>.</li>
            <li>An endpoint is <span className={strongText}>automatically disabled after 20 consecutive failures</span>. Fix the receiver, then re-enable it on the API Access page.</li>
            <li>Events are delivered in near real time but ordering is not guaranteed — use the payload's <code className={inlineCode}>created_at</code> when order matters.</li>
          </ul>
        </div>

        <div>
          <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${faintText}`}>Verifying signatures</h3>
          <p className={`text-xs mb-2 ${mutedText}`}>
            Every delivery includes the header{' '}
            <code className={inlineCode}>X-TutorTracer-Signature: sha256=&lt;hex&gt;</code>, where the hex value is{' '}
            <code className={inlineCode}>HMAC-SHA256(raw_body, endpoint_secret)</code>. Compute the HMAC over the <span className={strongText}>raw request body</span> (before any JSON parsing) and compare in constant time:
          </p>
          <CodeBlock code={NODE_VERIFY_SNIPPET} isDark={isDark} />
        </div>
      </section>
    </div>
  );
};

export default DeveloperApiDocs;
